import { useEffect, useState, type ReactElement } from 'react';
import { KiVorlage } from '@schnitt/shared';
import { api, type KiEinstellungenView, type VorlageView } from '../api';
import { useNachgeliefert } from '../nachgeliefert';
import { Sheet } from './Sheet';

type Anbieter = KiEinstellungenView['modell']['anbieter'];

const ANBIETER: { id: Anbieter; name: string; hinweis: string }[] = [
  { id: 'claude', name: 'Claude', hinweis: 'Schlüssel aus der Claude-Konsole. Nach aussen geht nur Text, kein Video.' },
  { id: 'ollama', name: 'Ollama (lokal)', hinweis: 'Läuft auf der Box, ohne Schlüssel. Reicht für „Moderation säubern“.' },
  { id: 'openai', name: 'OpenAI-kompatibel', hinweis: 'Jede Schnittstelle, die /v1/chat/completions spricht.' },
];

const STANDARD: Record<Anbieter | 'elevenlabs', string> = {
  elevenlabs: 'https://api.elevenlabs.io',
  claude: 'https://api.anthropic.com',
  ollama: 'http://ollama:11434',
  openai: 'https://api.openai.com',
};

/**
 * **Verwaltung → KI** (7.66; Sascha, 3. November: „Kann ich als Admin alles
 * in der Oberfläche anlegen?“).
 *
 * Drei Teile, von unten nach oben gebraucht: wer den Ton in Text verwandelt
 * (ElevenLabs), wer aus dem Text einen Schnitt vorschlägt (Claude, Ollama
 * oder eine OpenAI-kompatible Schnittstelle), und mit welchem Auftrag (die
 * KI-Vorlagen). Schlüssel gehen hinein und nie wieder heraus; „Verbindung
 * prüfen“ stellt eine echte Anfrage, weil nur die zeigt, ob Schlüssel,
 * Adresse und Modellname zusammenpassen.
 */
export function KiSettingsSheet({ onClose }: { onClose: () => void }): ReactElement {
  const [view, setView] = useState<KiEinstellungenView | null>(null);
  const [form, setForm, nachliefern] = useNachgeliefert({
    tAdresse: '',
    tSchluessel: '',
    anbieter: 'claude' as Anbieter,
    mAdresse: '',
    mName: '',
    mSchluessel: '',
  });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<Record<'transkription' | 'modell', { ok: boolean; meldung: string } | null>>({
    transkription: null,
    modell: null,
  });

  const laden = async () => {
    try {
      const { ki } = await api.kiEinstellungen();
      setView(ki);
      nachliefern((f) => ({
        ...f,
        tAdresse: ki.transkription.adresse,
        anbieter: ki.modell.anbieter,
        mAdresse: ki.modell.adresse,
        mName: ki.modell.name,
      }));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Die Einstellungen liessen sich nicht laden.');
    }
  };
  useEffect(() => {
    void laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eingabe = () => ({
    transkription: { adresse: form.tAdresse, schluessel: form.tSchluessel },
    modell: { anbieter: form.anbieter, adresse: form.mAdresse, name: form.mName, schluessel: form.mSchluessel },
  });

  const tun = async (was: () => Promise<unknown>) => {
    setBusy(true);
    setFehler(null);
    try {
      await was();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setBusy(false);
    }
  };

  const pruefen = (teil: 'transkription' | 'modell') =>
    void tun(async () => {
      const r = await api.kiPruefen({ teil, ...eingabe() });
      setErgebnis((e) => ({ ...e, [teil]: r }));
    });

  const gespeicherterSchluessel = view?.modell.anbieter === form.anbieter && view.modell.hatSchluessel;
  const anbieter = ANBIETER.find((a) => a.id === form.anbieter)!;

  return (
    <Sheet title="KI" onClose={onClose}>
      {fehler ? <p className="error-text">{fehler}</p> : null}
      {view?.unlesbar ? (
        <p className="hint" style={{ color: 'var(--err-text)' }}>
          Ein gespeicherter Schlüssel lässt sich nicht mehr entschlüsseln. Bitte neu eintragen.
        </p>
      ) : null}

      <h3 className="ki-titel">Transkription</h3>
      <div className="card" style={{ margin: '0 0 12px' }} data-ki-transkription={view?.transkription.eingerichtet ? 'ja' : 'nein'}>
        <div className="spread">
          <div>
            <div className="title">ElevenLabs</div>
            <div className="sub">Macht aus dem Ton Text mit Zeiten und Sprechern — die Grundlage für jeden KI-Schnitt.</div>
          </div>
          <span className={`badge ${view?.transkription.eingerichtet ? 'ok' : 'err'}`}>
            {view?.transkription.eingerichtet ? 'da' : 'fehlt'}
          </span>
        </div>
      </div>
      <label className="field">
        <span>Schlüssel</span>
        <input
          type="password"
          value={form.tSchluessel}
          data-ki-t-schluessel
          onChange={(e) => setForm({ ...form, tSchluessel: e.target.value })}
          placeholder={view?.transkription.hatSchluessel ? '•••••••• (bleibt, wenn leer)' : 'xi-…'}
          autoCapitalize="none"
        />
      </label>
      <label className="field">
        <span>Adresse (nur wenn abweichend)</span>
        <input
          value={form.tAdresse}
          onChange={(e) => setForm({ ...form, tAdresse: e.target.value })}
          placeholder={STANDARD.elevenlabs}
          autoCapitalize="none"
        />
      </label>
      {ergebnis.transkription ? (
        <p className="hint" data-ki-ergebnis="transkription" style={{ color: ergebnis.transkription.ok ? 'var(--ok-text)' : 'var(--err-text)' }}>
          {ergebnis.transkription.meldung}
        </p>
      ) : null}
      <button disabled={busy} data-ki-pruefen="transkription" onClick={() => pruefen('transkription')}>
        Verbindung prüfen
      </button>

      <h3 className="ki-titel">Modell</h3>
      <div className="card" style={{ margin: '0 0 12px' }} data-ki-modell={view?.modell.eingerichtet ? 'ja' : 'nein'}>
        <div className="spread">
          <div>
            <div className="title">{anbieter.name}</div>
            <div className="sub">{anbieter.hinweis}</div>
          </div>
          <span className={`badge ${view?.modell.eingerichtet ? 'ok' : 'err'}`}>{view?.modell.eingerichtet ? 'da' : 'fehlt'}</span>
        </div>
      </div>
      <div className="chips" style={{ marginBottom: 12 }}>
        {ANBIETER.map((a) => (
          <button
            key={a.id}
            className={`chip ${form.anbieter === a.id ? 'active' : ''}`}
            data-ki-anbieter={a.id}
            onClick={() => setForm({ ...form, anbieter: a.id })}
          >
            {a.name}
          </button>
        ))}
      </div>
      <label className="field">
        <span>Modellname</span>
        <input
          value={form.mName}
          data-ki-m-name
          onChange={(e) => setForm({ ...form, mName: e.target.value })}
          placeholder={form.anbieter === 'ollama' ? 'so, wie er in Ollama heisst' : 'so, wie ihn der Anbieter nennt'}
          autoCapitalize="none"
        />
      </label>
      {form.anbieter !== 'ollama' ? (
        <label className="field">
          <span>Schlüssel</span>
          <input
            type="password"
            value={form.mSchluessel}
            data-ki-m-schluessel
            onChange={(e) => setForm({ ...form, mSchluessel: e.target.value })}
            placeholder={gespeicherterSchluessel ? '•••••••• (bleibt, wenn leer)' : ''}
            autoCapitalize="none"
          />
        </label>
      ) : null}
      <label className="field">
        <span>{form.anbieter === 'ollama' ? 'Adresse' : 'Adresse (nur wenn abweichend)'}</span>
        <input
          value={form.mAdresse}
          onChange={(e) => setForm({ ...form, mAdresse: e.target.value })}
          placeholder={STANDARD[form.anbieter]}
          autoCapitalize="none"
        />
      </label>
      {ergebnis.modell ? (
        <p className="hint" data-ki-ergebnis="modell" style={{ color: ergebnis.modell.ok ? 'var(--ok-text)' : 'var(--err-text)' }}>
          {ergebnis.modell.meldung}
        </p>
      ) : null}
      <div className="stack">
        <button disabled={busy} data-ki-pruefen="modell" onClick={() => pruefen('modell')}>
          Verbindung prüfen
        </button>
        <button
          className="primary"
          disabled={busy}
          data-ki-speichern
          onClick={() =>
            void tun(async () => {
              const { ki } = await api.kiEinstellungenSichern(eingabe());
              setView(ki);
              setForm({ ...form, tSchluessel: '', mSchluessel: '' });
            })
          }
        >
          Speichern
        </button>
        {view?.transkription.eingerichtet || view?.modell.eingerichtet ? (
          <button
            className="ghost"
            disabled={busy}
            onClick={() =>
              void tun(async () => {
                const { ki } = await api.kiEinstellungenLoeschen();
                setView(ki);
                setErgebnis({ transkription: null, modell: null });
              })
            }
          >
            Einstellungen löschen
          </button>
        ) : null}
      </div>

      <KiVorlagen />
    </Sheet>
  );
}

const LEER: KiVorlage = { art: 'ki', modus: 'auswaehlen', anweisung: '', zielSekunden: null };

/**
 * Die KI-Vorlagen: der Auftrag an das Modell, einmal gut formuliert (Sascha:
 * „Glaube wir brauchen da Prompt-Vorlagen“). Drei kommen mit; geändert,
 * gelöscht und dazugelegt wird hier.
 */
function KiVorlagen(): ReactElement {
  const [liste, setListe] = useState<VorlageView[]>([]);
  const [offen, setOffen] = useState<{ alterName: string | null; name: string; daten: KiVorlage } | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const laden = async () => {
    try {
      setListe((await api.vorlagen('ki')).vorlagen);
    } catch {
      setMeldung('Die Vorlagen liessen sich nicht holen.');
    }
  };
  useEffect(() => {
    void laden();
  }, []);

  const sichern = async () => {
    if (!offen) return;
    const geprueft = KiVorlage.safeParse(offen.daten);
    if (!offen.name.trim() || !geprueft.success) {
      setMeldung('Name und Anweisung braucht jede Vorlage.');
      return;
    }
    setBusy(true);
    try {
      await api.vorlageSichern(offen.name.trim(), geprueft.data);
      // Umbenannt: die alte Fassung unter dem alten Namen geht.
      const alt = offen.alterName && offen.alterName.toLowerCase() !== offen.name.trim().toLowerCase()
        ? liste.find((v) => v.name === offen.alterName)
        : undefined;
      if (alt) await api.vorlageLoeschen(alt.id);
      setOffen(null);
      setMeldung(`„${offen.name.trim()}“ gesichert.`);
      await laden();
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : 'Das Sichern ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  const loeschen = async (v: VorlageView) => {
    setBusy(true);
    try {
      await api.vorlageLoeschen(v.id);
      setOffen(null);
      setMeldung(`„${v.name}“ gelöscht.`);
      await laden();
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : 'Löschen ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h3 className="ki-titel">Vorlagen</h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Der Auftrag an das Modell. „Nur herausnehmen“ lässt die Reihenfolge, wie sie ist; „Auswählen und ordnen“ baut neu.
      </p>
      {meldung ? (
        <p className="hint" data-ki-vorlagen-meldung>
          {meldung}
        </p>
      ) : null}
      <div className="list" data-ki-vorlagen={String(liste.length)}>
        {liste.map((v) =>
          offen?.alterName === v.name ? null : (
            <div className="row" key={v.id} data-ki-vorlage={v.name}>
              <div className="grow">
                <div className="title">{v.name}</div>
                <div className="sub">{v.satz}</div>
              </div>
              <button
                className="small"
                disabled={busy || v.daten?.art !== 'ki'}
                data-ki-vorlage-bearbeiten={v.name}
                onClick={() => v.daten?.art === 'ki' && setOffen({ alterName: v.name, name: v.name, daten: v.daten })}
              >
                Bearbeiten
              </button>
            </div>
          ),
        )}
      </div>
      {offen ? (
        <div className="card ki-vorlage-form" data-ki-vorlage-form>
          <label className="field">
            <span>Name</span>
            <input value={offen.name} data-ki-vorlage-name onChange={(e) => setOffen({ ...offen, name: e.target.value })} />
          </label>
          <div className="chips" style={{ marginBottom: 12 }}>
            {(
              [
                ['herausnehmen', 'Nur herausnehmen'],
                ['auswaehlen', 'Auswählen und ordnen'],
              ] as const
            ).map(([id, name]) => (
              <button
                key={id}
                className={`chip ${offen.daten.modus === id ? 'active' : ''}`}
                data-ki-vorlage-modus={id}
                onClick={() => setOffen({ ...offen, daten: { ...offen.daten, modus: id } })}
              >
                {name}
              </button>
            ))}
          </div>
          <label className="field">
            <span>Ziellänge in Sekunden (leer: egal)</span>
            <input
              inputMode="numeric"
              value={offen.daten.zielSekunden === null ? '' : String(offen.daten.zielSekunden)}
              data-ki-vorlage-ziel
              onChange={(e) => {
                const zahl = Number(e.target.value.replace(',', '.'));
                setOffen({ ...offen, daten: { ...offen.daten, zielSekunden: e.target.value.trim() && zahl > 0 ? zahl : null } });
              }}
            />
          </label>
          <label className="field">
            <span>Anweisung</span>
            <textarea
              rows={6}
              maxLength={4000}
              value={offen.daten.anweisung}
              data-ki-vorlage-anweisung
              onChange={(e) => setOffen({ ...offen, daten: { ...offen.daten, anweisung: e.target.value } })}
              placeholder="Was soll das Modell mit dem Material tun? Für wen, wie lang, was weglassen?"
            />
          </label>
          <div className="stack">
            <button className="primary" disabled={busy} data-ki-vorlage-sichern onClick={() => void sichern()}>
              Sichern
            </button>
            {offen.alterName ? (
              <button
                className="ghost"
                disabled={busy}
                onClick={() => {
                  const v = liste.find((x) => x.name === offen.alterName);
                  if (v) void loeschen(v);
                }}
              >
                Löschen
              </button>
            ) : null}
            <button className="ghost" disabled={busy} onClick={() => setOffen(null)}>
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <button
          className="small"
          style={{ marginTop: 8 }}
          data-ki-vorlage-neu
          disabled={busy}
          onClick={() => {
            setMeldung(null);
            setOffen({ alterName: null, name: '', daten: { ...LEER } });
          }}
        >
          Neue Vorlage
        </button>
      )}
    </>
  );
}
